// packages/shared/src/services/dossiers/emailEml.ts
// Génération d'un fichier .eml (RFC 5322 + MIME) côté navigateur : ouvrir un mail
// archivé dans Outlook (lecture formatée → « Répondre » natif). Module PUR — les
// pièces jointes arrivent déjà en base64, aucun réseau ici.

export interface EmlAttachment {
  filename: string;
  contentType: string;
  base64: string; // contenu base64, avec ou sans retours ligne (renormalisé ici)
}

export interface EmlInput {
  from: string;
  to: string[];
  cc: string[];
  subject: string | null;
  sentAt: string | null; // ISO
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: EmlAttachment[];
}

const CRLF = '\r\n';

/** UTF-8 → base64 replié à 76 colonnes (exigence MIME). */
export function toBase64Lines(input: string | Uint8Array): string {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin).replace(/(.{76})(?=.)/g, `$1${CRLF}`);
}

/** Neutralise l'injection d'en-têtes : supprime les caractères de contrôle (dont \r\n). */
function sanitizeAddress(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\x00-\x1f\x7f]/g, '').trim();
}

/** RFC 2047 : encode un en-tête s'il contient autre chose que de l'ASCII imprimable. */
export function encodeHeaderWord(value: string): string {
  if (/^[\x20-\x7e]*$/.test(value)) return value;
  const b64 = toBase64Lines(value).replace(/\r\n/g, '');
  return `=?utf-8?B?${b64}?=`;
}

export function rfc2822Date(iso: string): string {
  const d = new Date(iso);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const p = (n: number) => String(n).padStart(2, '0');
  return `${days[d.getUTCDay()]}, ${p(d.getUTCDate())} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()} `
    + `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} +0000`;
}

/** Neutralise les caractères de contrôle et les guillemets d'un nom de PJ (paramètres quotés MIME). */
function sanitizeAttachmentFilename(filename: string): string {
  // eslint-disable-next-line no-control-regex
  return filename.replace(/[\x00-\x1f\x7f]/g, '').replace(/"/g, "'");
}

/** Nom de fichier de téléchargement sûr, dérivé du sujet. */
export function sanitizeEmlFilename(subject: string | null): string {
  const base = (subject ?? 'message')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9 _-]+/g, ' ')
    .trim().replace(/\s+/g, '-').slice(0, 80);
  return `${base || 'message'}.eml`;
}

// Boundary statique : contenu contrôlé (mail déjà archivé), collision irréaliste,
// et le déterminisme garde le module pur/testable.
const BOUNDARY = 'cime-eml-boundary';

export function buildEml(input: EmlInput): string {
  const headers = [
    `From: ${sanitizeAddress(input.from)}`,
    input.to.length > 0 ? `To: ${input.to.map(sanitizeAddress).join(', ')}` : null,
    input.cc.length > 0 ? `Cc: ${input.cc.map(sanitizeAddress).join(', ')}` : null,
    `Subject: ${encodeHeaderWord(input.subject ?? '(sans objet)')}`,
    input.sentAt ? `Date: ${rfc2822Date(input.sentAt)}` : null,
    'MIME-Version: 1.0',
  ].filter((h): h is string => h !== null);

  const isHtml = Boolean(input.bodyHtml?.trim());
  const bodyType = isHtml ? 'text/html' : 'text/plain';
  const bodyData = isHtml ? String(input.bodyHtml) : (input.bodyText ?? '');
  const bodyHeaders = [
    `Content-Type: ${bodyType}; charset=utf-8`,
    'Content-Transfer-Encoding: base64',
  ];

  if (input.attachments.length === 0) {
    return [...headers, ...bodyHeaders, '', toBase64Lines(bodyData), ''].join(CRLF);
  }

  const parts = [
    [`--${BOUNDARY}`, ...bodyHeaders, '', toBase64Lines(bodyData)].join(CRLF),
    ...input.attachments.map((att) => {
      const name = encodeHeaderWord(sanitizeAttachmentFilename(att.filename));
      const b64 = att.base64.replace(/[\r\n]/g, '').replace(/(.{76})(?=.)/g, `$1${CRLF}`);
      return [
        `--${BOUNDARY}`,
        `Content-Type: ${att.contentType || 'application/octet-stream'}; name="${name}"`,
        'Content-Transfer-Encoding: base64',
        `Content-Disposition: attachment; filename="${name}"`,
        '',
        b64,
      ].join(CRLF);
    }),
  ];

  return [
    ...headers,
    `Content-Type: multipart/mixed; boundary="${BOUNDARY}"`,
    '',
    'Ce message est au format MIME.',
    '',
    ...parts,
    `--${BOUNDARY}--`,
    '',
  ].join(CRLF);
}
